// ===== NOUVEAU FICHIER : construction des formulaires de sessions =====
// Partagé par l'éditeur de sessions (ajout d'une ville, d'un jour, d'un créneau)
// et par le formulaire d'activité (chargement d'une activité à modifier).
// Un seul endroit pour les validateurs : impossible que les deux divergent.
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LieuDTO } from 'src/app/model/dto/activity-session';
import {
  heuresValides,
  minLengthArray,
  pasDeChevauchement,
} from 'src/app/modules/shared/validators/sessions.validators';

/** Un créneau saisi. L'id n'existe que pour un créneau déjà enregistré. */
export interface CreneauSaisi {
  id?: number | null;
  debut: string;
  fin: string;
  capacite?: number | null;
}

/** Une ville et ses jours (au moins un jour, sans chevauchement de créneaux) */
export function creerLieuForm(
  fb: FormBuilder,
  ville = '',
  jours: FormGroup[] = [],
): FormGroup {
  return fb.group(
    {
      ville: [ville, Validators.required],
      jours: fb.array(jours, minLengthArray(1)),
    },
    { validators: pasDeChevauchement },
  );
}

/** Un jour : sa date, sa salle et ses créneaux (au moins un) */
export function creerJourForm(
  fb: FormBuilder,
  date: string,
  salle: string | null,
  creneaux: CreneauSaisi[],
): FormGroup {
  return fb.group({
    date: [date, Validators.required],
    salle: [salle ?? ''],
    creneaux: fb.array(
      creneaux.map((c) => creerCreneauForm(fb, c)),
      minLengthArray(1),
    ),
  });
}

/** Un créneau. En modification, l'id permet au back de le mettre à jour au lieu de le recréer. */
export function creerCreneauForm(fb: FormBuilder, c: CreneauSaisi): FormGroup {
  return fb.group(
    {
      id: [c.id ?? null],
      debut: [c.debut, Validators.required],
      fin: [c.fin, Validators.required],
      capacite: [c.capacite ?? null, Validators.min(1)],
    },
    { validators: heuresValides },
  );
}

/** Lieux renvoyés par le back → contrôles du formulaire, avec les ids des créneaux */
export function lieuxVersFormulaire(
  fb: FormBuilder,
  lieux: LieuDTO[],
): FormGroup[] {
  return lieux.map((lieu) =>
    creerLieuForm(
      fb,
      lieu.ville,
      lieu.jours.map((jour) =>
        creerJourForm(
          fb,
          jour.date,
          jour.salle,
          jour.creneaux.map((c) => ({
            id: c.id,
            debut: heureCourte(c.debut),
            fin: heureCourte(c.fin),
            capacite: c.capacite,
          })),
        ),
      ),
    ),
  );
}

/** "09:00:00" (format du back) → "09:00" (format des champs heure et des comparaisons) */
function heureCourte(heure: string): string {
  return heure ? heure.slice(0, 5) : '';
}
